import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Trash2, RefreshCw, Eye, X } from 'lucide-react';

const STATUS_COLORS = {
  sent:         'bg-blue-100 text-blue-700',
  delivered:    'bg-green-100 text-green-700',
  failed:       'bg-red-100 text-red-700',
  bounced:      'bg-orange-100 text-orange-700',
  duplicate:    'bg-gray-100 text-gray-500',
  unsubscribed: 'bg-yellow-100 text-yellow-700',
};

export default function AdminEmailLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [previewLog, setPreviewLog] = useState(null); // { id, subject, to_email }
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/email-logs?${params}`, auth());
      setLogs(res.data);
    } catch {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [statusFilter]);

  const handleClear = async () => {
    if (!window.confirm('Clear all email logs? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/email-logs`, auth());
      toast.success(`Cleared ${res.data.deleted} records`);
      setLogs([]);
    } catch {
      toast.error('Failed to clear logs');
    }
  };

  const handlePreview = async (log) => {
    setPreviewLog(log);
    setPreviewHtml(null);
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/email-logs/${log.id}/body`, auth());
      setPreviewHtml(res.data.body_html);
    } catch {
      setPreviewHtml(null);
      toast.error('No body stored for this email');
    } finally {
      setPreviewLoading(false);
    }
  };

  const filtered = logs.filter(l => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return l.to_email?.toLowerCase().includes(q) || l.subject?.toLowerCase().includes(q) || l.template_key?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-playfair font-bold text-primary">Email Logs</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Delivery history for all outbound emails</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchLogs}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          <Button variant="destructive" size="sm" onClick={handleClear}><Trash2 className="w-3.5 h-3.5 mr-1" /> Clear All</Button>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="flex gap-3 flex-wrap items-center">
          <Input
            placeholder="Search email, subject, template…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs text-sm h-8"
          />
          <div className="flex gap-2 flex-wrap">
            {['', 'sent', 'delivered', 'failed', 'bounced', 'duplicate', 'unsubscribed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${statusFilter === s ? 'border-primary bg-primary text-white' : 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {s || 'All'}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground ml-auto">{filtered.length} records</span>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-muted-foreground">No email logs found.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>To</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">{log.to_email}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{log.subject}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{log.template_key || '—'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-600'}`}>
                          {log.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>
                        {log.has_body && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-50"
                            onClick={() => handlePreview(log)}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Email Preview Modal ── */}
      {previewLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between p-4 border-b">
              <div>
                <p className="text-sm font-semibold text-primary">{previewLog.subject}</p>
                <p className="text-xs text-muted-foreground mt-0.5">To: {previewLog.to_email}</p>
              </div>
              <button onClick={() => { setPreviewLog(null); setPreviewHtml(null); }}
                className="p-1 rounded hover:bg-gray-100 text-muted-foreground ml-4 flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {previewLoading ? (
                <div className="text-center text-muted-foreground py-12">Loading…</div>
              ) : previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full border rounded-lg"
                  style={{ height: '500px' }}
                  title="Email preview"
                  sandbox="allow-same-origin"
                />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-12">No body available for this email.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
